<template>
  <div>
    <div v-if="hasActiveLogEntry && ![TaskManagerTaskTypes.EXTRACT, TaskManagerTaskTypes.UPDATE].includes(taskType)">
      <log-entry-progress-bar
        :step="PipelineGeneralSteps.STARTED"
        label="STARTED"
        :task="getTask(PipelineGeneralSteps.STARTED)"
      />
      <log-entry-progress-bar
          v-for="(task, index) in nonStartStopTasks"
          :key="index"
          :label="getLabel(task.taskName)"
          :step="task.taskName"
          :task="task"
      />
      <log-entry-progress-bar
          :step="PipelineGeneralSteps.FINISHED"
          label="FINISHED"
          :task="getTask(PipelineGeneralSteps.FINISHED)"
      />
    </div>

    <!--Tasks-->
    <div v-else :class="hasActiveLogEntry ? 'mb-2' : 'inactive mb-2'">
      <log-entry-information
          class="mb-2 w-100"
          :vm-log-entries="[logEntry]"
          :show-status-block="false"
          :show-created-block="false"
          :show-finalized-items-only="false"
          :highlight-numbers-block="false"
      />

      <!--Lower part with wrong prediction-->
      <div v-if="($store.getters.isSuperAdmin || $store.getters.isSFXMember) && hasActiveLogEntry"
           class="d-flex mb-2 darker p-2 col-12 border-radius">
        {{ $t('pipeline.timePrediction') }} {{ predictConversionTime() }}
      </div>
      <log-entry-progress-bar
          v-for="(key, value, index) in PipelineSimplifiedExtractSteps"
          :key="index"
          :step="key"
          :label="$t('pipeline.' + value)"
          :task="getTask(key)"
      />
    </div>
  </div>
</template>

<script>
import {PipelineSimplifiedExtractSteps, TaskManagerTaskTypes, PipelineGeneralSteps} from "@/enum";
import DateMixin from "@/components/mixins/DateMixin.js.vue";
import LogEntryInformation from "@/components/pipeline/LogEntryInformation.vue";
import LogEntryProgressBar from "@/components/pipeline/LogEntryProgressBar.vue";
import vmLogEntry from "@/components/pipeline/vmLogEntry";
export default {
  name: "LogEntryProgress",
  components: {
    LogEntryInformation,
    LogEntryProgressBar
  },
  mixins: [DateMixin],
  props: {
    id: {type: String, required: true},
    isUpdate: {type: Boolean, default: false},
    logEntry: {type: Object, default: () => {}},
  },
  data() {
    return {
      PipelineGeneralSteps,
      PipelineSimplifiedExtractSteps,
      killSwitch: false,
      TaskManagerTaskTypes
    };
  },
  computed: {
    logEntryObject() {
      if(this.logEntry) {
        return new vmLogEntry(this.logEntry);
      }
      return {};
    },
    hasActiveLogEntry() {
      return this.logEntry && Object.keys(this.logEntry).length;
    },
    taskType() {
      return this.logEntryObject.getPipelineTask();
    },
    stats() {
      return this.logEntryObject.getStats();
    },
    nonStartStopTasks() {
      return this.stats ? this.stats.filter(task => {
        return ![PipelineGeneralSteps.STARTED, PipelineGeneralSteps.FINISHED].includes(task.taskName);
      }) : [];
    }
  },
  beforeDestroy() {
    this.killSwitch = true;
  },
  methods: {
    getLabel(taskName) {
      const mixed = Object.assign({}, this.PipelineSimplifiedExtractSteps, this.PipelineGeneralSteps);
      const found = Object.entries(mixed).find((entry) => entry[1] === taskName);
      let string = found ? found[0] : taskName;
      return this.$t('pipeline.' + string);
    },
    predictConversionTime() {
      if(!this.logEntry) {
        return;
      }
      const {vertices, groups, geometries} = this.logEntry;
      if (!vertices || !groups || !geometries) {
        return;
      }
      let time1 = this.predictConversionTimeForGeometries(geometries, vertices);
      let time2 = this.predictConversionTimeForGroups(groups, vertices);
      const total = time1 + time2;
      if(total < 0 || total < 60) {
        return 'Just a few minutes';
      }
      if (total < 60) {
        return total.toFixed(1) + ' minutes'
      } else {
        return (total / 60).toFixed(1) + ' hours';
      }
    },
    predictConversionTimeForGroups(groups, vertices) {
      const groupsCoefficient = 0.3236;
      const verticesCoefficient = 2.042e-5;
      const intercept = -53.16819076489077;

      return (groupsCoefficient * groups) + (verticesCoefficient * vertices) + intercept;
    },
    predictConversionTimeForGeometries(geometries, vertices) {
      const geometriesCoefficient = 0.01735;
      const verticesCoefficient = 2.042e-5;
      const intercept = -53.16819076489077;
      return (geometriesCoefficient * geometries) + (verticesCoefficient * vertices) + intercept;
    },
    getDuration(taskName) {
      const task = this.getTask(taskName);
      if (task && task.createdAt && task.updatedAt && task.createdAt !== task.updatedAt) {
        return this.timeDifferenceFromTwoISOs(task.updatedAt, task.createdAt).string;
      }
    },
    getProgress(taskName) {
      const task = this.getTask(taskName);
      if (task) {
        const progress = task.progress && typeof (parseFloat(task.progress)) === 'number' ? parseFloat(task.progress) : 0;
        return (progress * 100.0).toFixed(1);
      } else {
        return 0;
      }
    },
    getTask(taskName) {
      if (this.logEntry && this.logEntry.stats) {
        if(typeof this.logEntry.stats === 'string') {
          this.logEntry.stats = JSON.parse(this.logEntry.stats);
        }
        return this.logEntry.stats.find(item => {
          return item.taskName === taskName
        });
      }
      return null;
    },
    isDone(taskName) {
      const task = this.getTask(taskName);
      return !!task && task.progress && parseFloat(task.progress) === 1.0;
    },
  }
}
</script>

<style lang="scss" scoped>
.inactive {
  .log-task {
   opacity: 0.6;
  }
  .step-content {
    opacity: 0.3;
  }
}
.log-task {
  background-color: var(--vform-editor-ui-secondary-color);
  padding: 3px 6px;
  margin-bottom: 1px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.log-task-not-done {
  background-color: var(--vform-editor-ui-quinary-color);
}

.dz-progress {
  position: relative;
  width: 440px;
  padding: 0;
  height: 15px;
  margin-top: 2px;
  margin-bottom: -2px;

  .dz-upload {
    background-color: $highlight;
    height: 15px;

    p {
      position: absolute;
      left: 5px;
      top: 50%;
      -webkit-transform: translateY(-50%);
      transform: translateY(-50%);
    }
  }
}
</style>